import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { Client } from './client';

export enum WebSocketChannel {
  System = 'system',
  AppConfig = 'appconfig',
  Dataset = 'dataset/'
}

export interface WebSocketPublishMessage {
  channel: string;
  messageType?: string;
  payload: unknown;
}

export interface WebSocketSubscribeMessage {
  channel: string;
}

export interface WebSocketErrorResponse {
  status: 'error';
  code: string;
  message: string;
}

export interface WebSocketOkResponse {
  status: 'ok';
}

export type WebSocketResponse = WebSocketOkResponse | WebSocketErrorResponse;
export type WebSocketManagerOptions = Partial<ManagerOptions & SocketOptions>;
export type WebSocketSubscribeCallback = (err: Error | null, msg?: WebSocketPublishMessage) => void;
export type WebSocketUnsubscribeCallback = (err: Error | null) => void;
export type WebSocketUnsubscriber = (cb?: WebSocketUnsubscribeCallback) => void;

export class WebSocketGateway {
  private connected = false;
  private subscribedChannels = new Set<string>();

  socket!: Socket;

  get isConnected(): boolean {
    return this.connected;
  }

  constructor(public readonly client: Client, private readonly options?: WebSocketManagerOptions) {}

  connect(): Promise<void> {
    if (this.isConnected) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const socket = io(this.client.baseUrl, {
        ...this.options,
        autoConnect: false,
        auth: cb => {
          const authHeaders: Record<string, string> = {};

          if (this.client.apiKey) {
            authHeaders['x-api-key'] = this.client.apiKey;
          }

          if (this.client.accessToken) {
            authHeaders['x-socket-token'] = `Bearer ${this.client.accessToken}`;
          }

          cb(authHeaders);
        }
      });

      socket.on('connect', () => {
        this.client.debug(`Socket connected: ${socket.id}`);

        if (this.subscribedChannels.size) {
          this.subscribedChannels.forEach(channel => {
            socket.emit('subscribe', { channel }, (resp: WebSocketResponse) => {
              if (resp.status === 'error') {
                console.error(`Error re-subscribing to channel '${channel}`, resp);
              } else {
                this.client.debug(`Re-subscribed to channel '${channel}`);
              }
            });
          });
        }
      });

      socket.io.on('reconnect_attempt', (attempt: number) => {
        this.client.debug(`Socket reconnect attempt: ${attempt}`);
      });

      socket.io.on('reconnect_error', (error: Error) => {
        this.client.debug(`Socket reconnect error: ${error.message}`);
      });

      socket.io.on('reconnect_failed', () => {
        this.client.debug(`Socket reconnect failed`);
      });

      socket.io.on('reconnect', () => {
        this.client.debug(`Socket reconnected`);
      });

      socket.on('disconnect', reason => {
        this.client.debug(`Socket disconnected: ${reason}`);
      });

      socket.on('error', e => {
        this.client.debug(`Socket error: ${e.messaage}`);
      });

      this.socket = socket;

      this.connected = true;

      socket.on('connect', () => {
        resolve();
      });

      socket.connect();
    });
  }

  disconnect(): Promise<void> {
    if (!this.isConnected) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      this.socket.once('disconnect', () => {
        this.connected = false;
        resolve();
      });
      this.socket.disconnect();
    });
  }

  subscribe(channel: string, cb: WebSocketSubscribeCallback): WebSocketUnsubscriber {
    if (!this.isConnected) {
      throw new Error('WebSocketGateway is not initialized');
    }

    this.client.debug(`Subscribing to channel '${channel}`);

    // Discriminating 'message' event listener that only responds to messages sent to 'channel'
    const listener = (msg: WebSocketPublishMessage) => {
      if (msg.channel === channel) {
        cb(null, msg);
      }
    };

    // Unsubscriber
    const unsubscribe = (unsubscribeCb?: WebSocketUnsubscribeCallback) => {
      this.socket.off('message', listener);
      this.unsubscribe(channel, unsubscribeCb);
    };

    // Subscribe and bind listener
    this.socket.emit('subscribe', { channel }, (resp: WebSocketResponse) => {
      if (resp.status === 'error') {
        this.client.debug(`Error subscribed to channel '${channel}`, resp);
        return cb(new Error(resp.message));
      }

      this.subscribedChannels.add(channel);
      this.client.debug(`Subscribed to channel '${channel}`);
      this.socket.on('message', listener);
    });

    return unsubscribe;
  }

  unsubscribe(channel: string, cb?: WebSocketUnsubscribeCallback) {
    if (!this.isConnected) {
      throw new Error('WebSocketGateway is not initialized');
    }

    this.client.debug(`Unsubscribing from channel '${channel}`);
    this.socket.emit('unsubscribe', { channel }, (resp: WebSocketResponse) => {
      if (resp.status === 'error') {
        this.client.debug(`Error unsubscribing from channel '${channel}`, resp);
        if (cb) {
          cb(new Error(resp.message));
        }
      } else {
        this.subscribedChannels.delete(channel);
        this.client.debug(`Unsubscribed from channel '${channel}`);
        if (cb) {
          cb(null);
        }
      }
    });
  }
}
